/** @format */
import isPropValid from '@emotion/is-prop-valid';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { animated, useTransition } from '@react-spring/web';
import React, { FunctionComponent, useMemo, useRef } from 'react';
import styled from 'styled-components';

import CloseIcon from '@toucan/icons/close.svg';

import BtnCurve from '@toucan/images/modal/btn-curve.svg';

import { useFocusTrap } from '@toucan/client/global/application/hooks/use-focus-trap';
// eslint-disable-next-line boundaries/element-types -- This should be using the feather version of the hooks
import { useClickOutside } from '@toucan/client/shared/hooks/use-click-outside';
// eslint-disable-next-line boundaries/element-types -- This should be using the feather version of the hooks
import { useKeyPress } from '@toucan/client/shared/hooks/use-key-press';

import { Background } from './components/background';
import { DEFAULT_TEST_ID, ModalVariant } from './constants';
import { styles } from './modal.styles';
import { ModalProps } from './types';

export * from './constants';

const StyledModal = styled(animated.div).withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const Modal: FunctionComponent<ModalProps> = ({
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    children,
    size,
    onClose,
    closeLabel,
    isClosable = true,
    isOpen = false,
    variant = ModalVariant.DEFAULT,
    ...otherProps
}: ModalProps) => {
    const bodyRef = useRef(
        typeof document !== 'undefined' ? document.body : null
    );
    const modalRef = useRef(null);
    const transitions = useTransition(isOpen, {
        config: {
            duration: 140,
        },
        from: {
            transform: 'translate3d(-50%, -47%, 0)',
            opacity: 0,
        },
        enter: {
            transform: 'translate3d(-50%, -50%, 0)',
            opacity: 1,
        },
        leave: {
            transform: 'translate3d(-50%, -47%, 0)',
            opacity: 0,
        },
    });

    /**
     * Renders a close button for the modal
     */
    function renderCloseButton() {
        if (!isClosable) {
            return null;
        }

        return (
            <Icon
                title={closeLabel ?? ''}
                onClick={() => onClose?.()}
                variant={IconVariant.NAVIGATION}
                size={IconSize.M}
                data-testid="modal-close-button"
                className="modal-close"
            >
                <CloseIcon />
            </Icon>
        );
    }

    const closeButton = useMemo(renderCloseButton, [onClose, size, isClosable]);

    /**
     * Trap focus within the modal, adding isOpen to the
     * list of watches the hook uses
     */
    useFocusTrap(modalRef, [isOpen]);

    /**
     * Handle an escape keypress, invoking the onClose prop if the drawer
     * is currently open.
     */
    useKeyPress(bodyRef.current, ['Escape', 'Esc'], () => {
        if (!isClosable) {
            return;
        }

        isOpen && onClose && onClose();
    });

    /**
     * Handle clicks outside the drawer, invoking the onClose prop if the drawer
     * is currently open.
     */
    useClickOutside(modalRef, () => {
        if (!isClosable) {
            return;
        }

        isOpen && onClose && onClose();
    });
    return (
        <React.Fragment>
            {transitions((props, item) =>
                item ? (
                    <StyledModal
                        {...otherProps}
                        style={props}
                        size={size}
                        ref={modalRef}
                        isClosable={isClosable}
                        variant={variant}
                        data-testid={dataTestId}
                    >
                        <div className="header" data-testid="modal-header">
                            <BtnCurve className="curved-svg" />
                            {closeButton}
                        </div>
                        <Background
                            data-testid={`${dataTestId}__background`}
                            variant={variant}
                        >
                            <div
                                className="contents"
                                data-testid="modal-content"
                            >
                                {children}
                            </div>
                        </Background>
                    </StyledModal>
                ) : null
            )}
        </React.Fragment>
    );
};
